import { supabase } from './authService';
import { TeamSelection } from '../types';
import { getTeamLogo, getLeagueLogo } from './footballService';

export const favoriteService = {
  getFavoriteTeamRanking: async (limit: number = 10): Promise<{team_id: string, team_name: string, count: number, logo: string}[]> => {
    try {
      // Fetch teams_selections first, then fetch team details separately
      const { data: selectionsData, error: selectionsError } = await supabase
        .from('teams_selections')
        .select('team_id');
        
      if (selectionsError) throw selectionsError;

      const teamCounts: Record<string, number> = {};
      selectionsData?.forEach((item: any) => {
        if (!item.team_id) return;
        teamCounts[item.team_id] = (teamCounts[item.team_id] || 0) + 1;
      });

      // Get top team IDs
      const topTeamIds = Object.keys(teamCounts)
        .sort((a, b) => teamCounts[b] - teamCounts[a])
        .slice(0, limit);

      if (topTeamIds.length === 0) return [];

      // Fetch team names for top teams
      return topTeamIds.map(team_id => ({
        team_id,
        team_name: `Team ${team_id}`,
        count: teamCounts[team_id],
        logo: getTeamLogo(team_id)
      }));
    } catch (error) {
      console.error('Error fetching favorite team ranking:', error);
      return [];
    }
  },

  getFavoriteTeams: async (): Promise<TeamSelection[]> => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];

      const { data, error } = await supabase
        .from('teams_selections')
        .select('*')
        .eq('user_id', user.id);

      if (error) throw error;
      return (data || []).map((item: any) => ({
        ...item,
        team: item.team ? {
          ...item.team,
          logo: getTeamLogo(item.team_id)
        } : undefined
      }));
    } catch (error) {
      console.error('Error fetching favorite teams:', error);
      return [];
    }
  },

  toggleFavoriteTeam: async (teamId: string, isFavorite: boolean): Promise<boolean> => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return false;

      // If setting as default team (true), unset existing default team first
      if (isFavorite) {
        await supabase
          .from('teams_selections')
          .update({ is_favorite: false })
          .eq('user_id', user.id)
          .eq('is_favorite', true);
      }

      const { data: existing } = await supabase
        .from('teams_selections')
        .select('id')
        .eq('user_id', user.id)
        .eq('team_id', teamId)
        .maybeSingle();

      if (existing) {
        const { error } = await supabase
          .from('teams_selections')
          .update({ is_favorite: isFavorite })
          .eq('id', existing.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('teams_selections')
          .insert([{
            user_id: user.id,
            team_id: teamId,
            is_favorite: isFavorite
          }]);
        if (error) throw error;
      }

      return true;
    } catch (error) {
      console.error('Error toggling favorite team:', error);
      return false;
    }
  },

  addOnlyFavorite: async (teamId: string): Promise<boolean> => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return false;

      const { data: existing } = await supabase
        .from('teams_selections')
        .select('id, is_favorite')
        .eq('user_id', user.id)
        .eq('team_id', teamId)
        .maybeSingle();

      if (!existing) {
        const { error } = await supabase
          .from('teams_selections')
          .insert([{
            user_id: user.id,
            team_id: teamId,
            is_favorite: false
          }]);
        if (error) throw error;
      }
      return true;
    } catch (error) {
      console.error('Error adding favorite team:', error);
      return false;
    }
  },

  removeFavorite: async (teamId: string): Promise<boolean> => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return false;

      const { error } = await supabase
        .from('teams_selections')
        .delete()
        .eq('user_id', user.id)
        .eq('team_id', teamId);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error removing favorite team:', error);
      return false;
    }
  },

  isFavoriteTeam: async (teamId: string): Promise<boolean> => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return false;

      const { data, error } = await supabase
        .from('teams_selections')
        .select('is_favorite')
        .eq('user_id', user.id)
        .eq('team_id', teamId)
        .maybeSingle();

      if (error) throw error;
      return data?.is_favorite || false;
    } catch (error) {
      console.error('Error checking favorite team status:', error);
      return false;
    }
  }
};
